/**
 * React Provider for Babylon.js integration
 */

import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { PartialBabylonConfig } from "../config/schemas";
import { BabylonManager } from "../managers/BabylonManager";
import { BabylonContext } from "./BabylonContext";
import type { BabylonProviderProps } from "./types";

/**
 * Tracks active provider instances to manage singleton disposal.
 */
let activeProviderCount = 0;

/**
 * Provider component for Babylon.js manager
 * Provides a BabylonManager instance to child components
 */
export const BabylonProvider: React.FC<BabylonProviderProps> = ({
  children,
  config,
  autoInitialize = true,
  allowMultipleInstances = false,
  onInitialized,
  onError,
}) => {
  // State for initialization tracking
  const [isInitialized, setIsInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const initializationRef = useRef(false);
  const hasSignaledReadyRef = useRef(false);

  // Create manager instance - singleton or new instance based on prop
  const manager = useMemo(() => {
    // If allowing multiple instances, create a new manager
    // Otherwise use the singleton pattern
    return BabylonManager.getInstance(!allowMultipleInstances);
  }, [allowMultipleInstances]);

  // Generate a unique scene ID for multi-instance scenarios
  const sceneId = useMemo(() => {
    if (allowMultipleInstances) {
      return `scene_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
    return undefined;
  }, [allowMultipleInstances]);

  // Initialize function
  const initialize = useCallback(
    async (customConfig?: PartialBabylonConfig) => {
      setError(null);

      if (manager.isInitialized) {
        if (!isInitialized) {
          setIsInitialized(true);
        }
        return;
      }

      if (initializationRef.current || isInitializing) {
        return;
      }

      initializationRef.current = true;

      if (manager.isInitializing) {
        setIsInitializing(true);
        try {
          await manager.waitForReady();
          setIsInitialized(true);
        } catch (err) {
          const error =
            err instanceof Error
              ? err
              : new Error("Failed to initialize Babylon");
          setError(error);
          onError?.(error);
          console.error("Failed to initialize BabylonManager:", error);
        } finally {
          setIsInitializing(false);
          initializationRef.current = false;
        }
        return;
      }

      setIsInitializing(true);

      try {
        await manager.initialize(customConfig || config);
        setIsInitialized(true);
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error("Failed to initialize Babylon");
        if (error.message.includes("already initializing")) {
          try {
            await manager.waitForReady();
            setIsInitialized(true);
            setError(null);
            return;
          } catch (waitError) {
            const wrappedError =
              waitError instanceof Error
                ? waitError
                : new Error("Failed to initialize Babylon");
            setError(wrappedError);
            onError?.(wrappedError);
            console.error("Failed to initialize BabylonManager:", wrappedError);
            return;
          }
        }

        setError(error);
        onError?.(error);
        console.error("Failed to initialize BabylonManager:", error);
      } finally {
        setIsInitializing(false);
        initializationRef.current = false;
      }
    },
    [manager, config, isInitialized, isInitializing, onInitialized, onError],
  );

  // Auto-initialize on mount if requested
  useEffect(() => {
    if (autoInitialize && !isInitialized && !isInitializing) {
      initialize();
    }
  }, [autoInitialize, isInitialized, isInitializing, initialize]);

  // Track provider instances for debugging, but DON'T dispose singleton on unmount.
  // The singleton should persist for the app's lifetime to avoid race conditions
  // during tab navigation where React mounts new components before unmounting old ones.
  useEffect(() => {
    if (!allowMultipleInstances) {
      activeProviderCount += 1;
    }

    return () => {
      if (!allowMultipleInstances) {
        activeProviderCount = Math.max(0, activeProviderCount - 1);
        // NOTE: We intentionally do NOT dispose the singleton here.
        // Disposing during tab navigation causes race conditions where
        // the new tab's provider receives a disposed manager reference.
      } else {
        // For multi-instance mode, always dispose on unmount
        if (manager?.isInitialized) {
          try {
            manager.dispose();
          } catch (err) {
            console.error("Error disposing BabylonManager:", err);
          }
        }
      }
    };
  }, [manager, allowMultipleInstances]);

  // Dispose function
  const dispose = useCallback(() => {
    if (manager?.isInitialized || manager?.isInitializing) {
      try {
        manager.dispose();
        setIsInitialized(false);
        initializationRef.current = false;
        setIsInitializing(false);
        activeProviderCount = 0;
        hasSignaledReadyRef.current = false;
      } catch (err) {
        console.error("Error disposing BabylonManager:", err);
      }
    }
  }, [manager]);

  // Sync state with manager lifecycle events
  useEffect(() => {
    if (manager.isInitialized) {
      setIsInitialized(true);
      if (!hasSignaledReadyRef.current) {
        hasSignaledReadyRef.current = true;
        onInitialized?.(manager);
      }
    }

    const unsubscribeReady = manager.onEvent("ready", () => {
      setIsInitializing(false);
      setIsInitialized(true);
      if (!hasSignaledReadyRef.current) {
        hasSignaledReadyRef.current = true;
        onInitialized?.(manager);
      }
    });

    const unsubscribeDisposed = manager.onEvent("disposed", () => {
      setIsInitializing(false);
      setIsInitialized(false);
      hasSignaledReadyRef.current = false;
    });

    return () => {
      unsubscribeReady();
      unsubscribeDisposed();
    };
  }, [manager, onInitialized]);

  // Create context value with functions
  const contextValue = useMemo(
    () => ({
      dispose,
      error,
      initialize,
      isInitialized,
      isInitializing,
      manager,
      sceneId,
    }),
    [
      manager,
      isInitialized,
      isInitializing,
      error,
      initialize,
      dispose,
      sceneId,
    ],
  );

  return (
    <BabylonContext.Provider value={contextValue}>
      {children}
    </BabylonContext.Provider>
  );
};

/**
 * Display name for debugging
 */
BabylonProvider.displayName = "BabylonProvider";
